import { useEffect, useMemo, useState } from "react";

import { VerificationService } from "@/modules/verification/services/verificationService";

import { ClientsService } from "../services/clientsService";
import type { ClientByIdResponse, OrganizationDetailResponse } from "../types/clients-api.types";
import { mapOrganizationDetailToViewClient } from "../utils/mapOrganizationDetailToViewClient";

export interface UseOrganizationKycProfileReturn {
  /** The organization rendered in the client-detail shape `KycVerificationSection` consumes. */
  profile: ClientByIdResponse | null;
  isLoading: boolean;
}

/** Result of one resolution, tagged with its organization so a stale one is never read. */
interface ResolvedOrganizationKyc {
  organizationId: string;
  profile: ClientByIdResponse | null;
  identityId: string | null;
}

/**
 * Corporate KYC read model for the organization detail page.
 *
 * Three sources, each covering a gap of the previous one:
 *
 * 1. `GET /internal/clients/organizations/:id/client-profile` — the real corporate KYC (status, verified_at,
 *    documents). The tree detail hardcodes `kyc_status: null` at the organization level on qa, so it cannot
 *    drive this section on its own.
 * 2. `GET /internal/identities/verification/organizations/:id/status` — the corporate `identity_id`, which no
 *    clients endpoint exposes and which the section needs to issue a verification link.
 * 3. The already-loaded tree detail, mapped locally, as the fallback view when (1) fails or is still in flight.
 *
 * Both remote calls are best-effort: a failure degrades the section (informative state) instead of breaking
 * the page. Keyed by `organizationId` only, so refreshing the clients list does not re-issue them.
 */
export function useOrganizationKycProfile(
  organizationId: string | undefined,
  organization: OrganizationDetailResponse | null,
): UseOrganizationKycProfileReturn {
  const [resolved, setResolved] = useState<ResolvedOrganizationKyc | null>(null);

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;
    (async () => {
      let profile: ClientByIdResponse | null = null;
      try {
        const { data } = await ClientsService.fetchOrganizationClientProfile(organizationId);
        profile = data;
      } catch {
        // keep null — the tree-derived view takes over
      }

      let identityId = profile?.identity_id ?? null;
      if (!identityId) {
        try {
          const { data } = await VerificationService.getOrganizationIdentityStatus(organizationId);
          identityId = data.identity_id ?? null;
        } catch {
          // keep null — the section falls back to its informative state
        }
      }

      if (cancelled) return;
      setResolved({ organizationId, profile, identityId });
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // A result from a previously viewed organization must never leak into this render.
  const current = resolved?.organizationId === organizationId ? resolved : null;

  const profile = useMemo(() => {
    const base = current?.profile ?? (organization ? mapOrganizationDetailToViewClient(organization) : null);
    if (!base) return null;
    const identityId = current?.identityId ?? base.identity_id ?? null;
    return base.identity_id === identityId ? base : { ...base, identity_id: identityId };
  }, [current, organization]);

  return { profile, isLoading: Boolean(organizationId) && current === null };
}
